import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";

import type { ResumeContextValue } from "@/app/types/resume";

import CandidateSupportModal from "../../components/CandidateSupportModal/CandidateSupportModal";
import { ResumeDataService } from "../../services/create-resume/resume-data";
import ResumeFeedbackModal, { type ResumeFeedbackItem } from "../../Views/ResumeBuilder/ResumeFeedbackModal";
import ResumePreviewModal from "../../Views/ResumeBuilder/ResumePreviewModal";
import ResumeContext from "../ResumeContext";

export interface ResumeBuilderActions {
	/** Open the resume preview dialog. */
	openPreview: () => void;
	/** Open the review-feedback panel; fetches feedback on first open. */
	openFeedback: () => void;
	/** Open the candidate support drawer. */
	openSupport: () => void;
}

const ResumeBuilderActionsContext = createContext<ResumeBuilderActions | null>(null);

interface FeedbackReducerState {
	feedBacks: ResumeFeedbackItem[];
	[key: string]: unknown;
}

/**
 * Owns the open state for the three shared resume-builder dialogs (preview,
 * feedback, support) and renders them. Pages and the layout trigger them
 * through `useResumeBuilderActions()` instead of Bootstrap `data-toggle`.
 */
export function ResumeBuilderActionsProvider({ children }: { children: React.ReactNode }) {
	const { resumeData } = useContext(ResumeContext) as ResumeContextValue;

	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
	const [isSupportOpen, setIsSupportOpen] = useState(false);
	const [feedbackLoading, setFeedbackLoading] = useState(false);

	const dispatch = useDispatch();
	const { feedBacks } = useSelector((state: { FeedbackReducers: FeedbackReducerState }) => state.FeedbackReducers);

	const dispatchFeedbackState = useCallback(
		(name: string, value: unknown) => {
			dispatch({ type: "UPDATE_FEEDBACK", payload: { name, value } });
		},
		[dispatch],
	);

	const getReviewFeedBack = useCallback(
		(reviewRequestId: string) => {
			setFeedbackLoading(true);
			dispatchFeedbackState("feedBacks", []);
			dispatchFeedbackState("feedBackSearched", false);

			const resumeDataService = new ResumeDataService();
			resumeDataService.getResumeFeedback(
				reviewRequestId,
				(response: { data: Record<string, ResumeFeedbackItem[]> }) => {
					const _feedBacks: ResumeFeedbackItem[] = [];
					const feedBackDetails = response.data;

					for (const key in feedBackDetails) {
						feedBackDetails[key].forEach((feedBackDetail) => {
							_feedBacks.push(feedBackDetail);
						});
					}

					setFeedbackLoading(false);
					dispatchFeedbackState("feedBacks", _feedBacks);
					dispatchFeedbackState("feedBackSearched", true);
				},
				() => {
					setFeedbackLoading(false);
					toast.error("Something went wrong!");
					dispatchFeedbackState("feedBackSearched", true);
				},
			);
		},
		[dispatchFeedbackState],
	);

	const openPreview = useCallback(() => {
		setIsPreviewOpen(true);
	}, []);

	const openFeedback = useCallback(() => {
		if (!isFeedbackOpen && feedBacks.length === 0 && resumeData.reviewRequests) {
			const _reviewRequests = resumeData.reviewRequests.filter((reviewRequest) => reviewRequest.status === "PUBLISHED");

			if (_reviewRequests?.[0]?._id) {
				getReviewFeedBack(_reviewRequests[0]._id);
			}
		}

		setIsFeedbackOpen(true);
	}, [isFeedbackOpen, feedBacks.length, resumeData.reviewRequests, getReviewFeedBack]);

	const openSupport = useCallback(() => {
		setIsSupportOpen(true);
	}, []);

	const actions = useMemo<ResumeBuilderActions>(
		() => ({ openPreview, openFeedback, openSupport }),
		[openPreview, openFeedback, openSupport],
	);

	return (
		<ResumeBuilderActionsContext.Provider value={actions}>
			{children}
			<ResumePreviewModal resumeData={resumeData} open={isPreviewOpen} onOpenChange={setIsPreviewOpen} />
			<ResumeFeedbackModal
				feedbacks={feedBacks}
				isFeedbackOpen={isFeedbackOpen}
				feedbackLoading={feedbackLoading}
				toggleFeedbackModal={() => setIsFeedbackOpen((open) => !open)}
			/>
			{isSupportOpen && <CandidateSupportModal show={isSupportOpen} close={() => setIsSupportOpen(false)} />}
		</ResumeBuilderActionsContext.Provider>
	);
}

/**
 * Access the shared resume-builder dialog actions. Must be called inside
 * `ResumeBuilderActionsProvider` (rendered by `ResumeBuilderLayout`).
 */
export function useResumeBuilderActions(): ResumeBuilderActions {
	const context = useContext(ResumeBuilderActionsContext);

	if (!context) {
		throw new Error(
			"useResumeBuilderActions must be used inside <ResumeBuilderActionsProvider> (rendered by ResumeBuilderLayout on /resume/* routes).",
		);
	}

	return context;
}

/**
 * Like `useResumeBuilderActions`, but returns null outside the provider.
 * For components shared with legacy Bootstrap routes (e.g. the ResumePreview
 * thumbnail on MyResumes), which fall back to their Bootstrap triggers.
 */
export function useResumeBuilderActionsOptional(): ResumeBuilderActions | null {
	return useContext(ResumeBuilderActionsContext);
}

export default ResumeBuilderActionsContext;
